import React, { useState, useMemo, useRef, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import { Popover, Modal } from 'bootstrap';

import Icon from '../components/icon';

const STEPS = [
	'welcome',
	'popover-dashboard',
	'popover-connector',
	'popover-documentation',
	// 'popover-account', // optional
	'connector',
];

const POPOVERS = {
	'popover-dashboard':
		'In the dashboard, you can view a log of all activity in your account, including connected brokers, created webhooks, and sent webhooks.',
	'popover-connector':
		'Here you can connect brokers and create webhook URL’s.  You will need a unique webhook URL for each account that you wish to have trades sent too.',
	'popover-account':
		'Here you can manage your subscription.',
	'popover-documentation':
		'Here, you can build your order instructions with NO CODE. Simply select your broker and order settings, and the builder will show you an example of how to format your JSON message correctly.'
};

const POPOVER_BUTTON_ID = 'button-welcome-popover-continue';

// overcomplicated and fragile:
// to handle modals and tricky popovers for offcanvas menu views
function MeDashboardWelcome({ step: defaultStep }) {
	const [ step, setStep ] = useState(defaultStep ?? 'welcome');

	const menuVisiblity = useMemo(() =>
		document.getElementsByClassName('navbar-toggler-icon')?.[0]?.checkVisibility?.() // weak
	, []);

	const menuRef = useRef(null);

	const onNext = nextStep => {
		setStep(prevStep =>
			nextStep ?? STEPS[STEPS.indexOf(prevStep) + 1]
		);
	};

	useEffect(() => {
		switch (step) {
			case 'welcome':
				Modal.getOrCreateInstance(document.getElementById('stepWelcomeModal'))?.show();
				break;

			case 'connector':
				Modal.getOrCreateInstance(document.getElementById('stepConnectorModal'))?.show();
				break;

			case 'popover-dashboard':
			case 'popover-connector':
			case 'popover-account':

			case 'popover-documentation': {
				// ex: 'id-popover-dashboard'
				const popoverElem = menuRef.current.getElementsByClassName('id-' + step)[0];

				const popover = Popover.getOrCreateInstance(popoverElem, {
					html: true,
					sanitize: false,
					placement: 'right',
					content: POPOVERS[step] + `
						<div class="mt-2 text-end">
							<button id="${POPOVER_BUTTON_ID + step}" type="button" class="btn btn-primary btn-sm">
								Continue
							</button>
						<div>
					`
				});

				popover.show();

				document.
					getElementById(POPOVER_BUTTON_ID + step).
					addEventListener('click', () => {
						popover.hide();
						onNext();
					});

				popoverElem.addEventListener('hidden.bs.popover', () => {
					popover.dispose?.();
				});

				break;
			}

			default:
				// pass
				// to dismiss anything else?
		}
	}, [step]);

	return (
		<div>
			<div className="modal" id="stepWelcomeModal" tabIndex="-1" data-bs-backdrop="static" data-bs-keyboard="false" aria-labelledby="stepWelcomeModalLabel">
				<div className="modal-dialog modal-dialog-centered text-start">
					<div className="modal-content">
						<div className="modal-header">
							<h5 className="modal-title" id="stepWelcomeModalLabel">Welcome</h5>
							<button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close" onClick={() => setStep(null)} />
						</div>

						<div className="modal-body">
							<p>Welcome to SignalStack, the automated trading platform that helps you take your trading to the next level.</p>
							<p>This short tour will introduce you to the SignalStack platform and show you how to get started with automated trading.</p>
							<div className="mt-3 text-end"><button type="button" className="btn btn-primary" data-bs-dismiss="modal" onClick={() => onNext()}>Continue</button></div>
						</div>
					</div>
				</div>
			</div>

			<div className="modal" id="stepConnectorModal" tabIndex="-1" data-bs-backdrop="static" data-bs-keyboard="false" aria-labelledby="stepConnectorModalLabel">
				<div className="modal-dialog modal-dialog-centered text-start">
					<div className="modal-content">
						<div className="modal-header">
							<h5 className="modal-title" id="stepConnectorModalLabel">Brokers</h5>
							<button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close" onClick={() => setStep(null)} />
						</div>

						<div className="modal-body">
							<p>Connect a broker to setup automated trading.</p>
							<NavLink to="/me/connector" className="d-grid gap-2"><button className="btn btn-primary btn-block" data-bs-dismiss="modal" onClick={() => setStep(null)}>Continue</button></NavLink>
						</div>
					</div>
				</div>
			</div>

			<div ref={menuRef}>
				{/* popovers: mimicking navbar menu */}
				{
					step && (
						<nav className={'sidebar' + (menuVisiblity && step?.startsWith('popover') ? '' : ' opacity-0')}>
							<div className="offcanvas offcanvas-start show">
								<div className="offcanvas-header d-none d-lg-block py-3 px-4">
									<img src="/static/images/logo-black.svg" style={{width: '10em', /* width: '75%' */}} className="my-1" />
								</div>
								<div className="offcanvas-header d-lg-none">
									<h5 className="offcanvas-title">Menu</h5>
								</div>

								<div className="offcanvas-body px-0 pt-0">
									<ul className="nav nav-pills flex-column m-2 mb-5 mb-lg-auto">
										<li className="nav-item id-popover-dashboard">
											<a title="Dashboard" className="nav-link">
												<Icon.ClipboardPulse />
												<span className="nav-link-text">Dashboard</span>
											</a>
										</li>
										<li className="nav-item id-popover-connector">
											<a title="Brokers and Webhooks" className="nav-link">
												<Icon.CardList />
												<span className="nav-link-text">Brokers &amp; Webhooks</span>
											</a>
										</li>
										<li className="nav-item id-popover-account">
											<a title="Account and Billing" className="nav-link">
												<Icon.Person />
												<span className="nav-link-text">Account &amp; Billing</span>
											</a>
										</li>
										<li className="nav-item id-popover-documentation">
											<a title="Documentation" className="nav-link">
												<Icon.QuestionCircle />
												<span className="nav-link-text">Documentation</span>
											</a>
										</li>
									</ul>
								</div>
							</div>
						</nav>
					)
				}

				{/* popovers: mimicking backdrop */}
				<div className={'modal-backdrop show' + (step?.startsWith('popover') ? '' : ' d-none')} />
			</div>
		</div>
	);
}

export default MeDashboardWelcome;
